import React, {Component} from "react";
import SimpleReactValidator from "simple-react-validator";

/* Import Components */
import CheckBox from "./CheckBox";
import Input from "./Input";
import TextArea from "./TextArea";
import Select from "./Select";
import Button from "./Button";
import EmailInput from "./EmailInput";
import RadioButton from "./RadioButton";
import TelephoneInput from "./TelephoneInput";
import FetchHelper from "./helpers/FetchHelper";
import Confirmation from "./Confirmation";
import ResearchDisclaimer from "./ResearchDisclaimer";

class NhsReferral extends Component {
    constructor(props) {
        super(props);

        this.state = {
            initialState: true,
            loading: false,
            submitted: false,
            newUser: {system: "NHS"},
            genderOptions: ["Male", "Female", "Non-binary", "Prefer not to say"],
            contactOptions: ["Telephone", "Email", "Post", "Other"],
            yesNoOptions: ["Yes", "No"],
            medications: [],
            practiceOptions: []
        };

        const validateNumber = (phone_number) => {
            if (!phone_number || phone_number?.length === 0) {
                return {
                    valid: true,
                    message: "phone number not required"
                }
            } else {
                if (phone_number?.startsWith('07') || phone_number?.startsWith('+44')) {
                    let numberLength = phone_number.length || 0
                    if (phone_number?.startsWith('0')) {
                        if (numberLength !== 11) {
                            return {
                                valid: false,
                                message: `UK phone number starting with (0) must be 11 characters, currently ${numberLength} chars.`
                            }
                        }
                    }
                    if (phone_number?.startsWith('+44')) {
                        if (numberLength !== 13) {
                            return {
                                valid: false,
                                message: `UK phone number starting with (+44) must be 13 characters, currently ${numberLength} chars`
                            }
                        }
                    }
                    if (!phone_number?.match(/^[+0-9]*$/)) {
                        return {
                            valid: false,
                            message: "A phone number can only have numbers or +"
                        }
                    }
                } else {
                    return {
                        valid: false,
                        message: `UK phone number must start with either (+44) or (07)`
                    }
                }
            }
            return {
                valid: true,
            }
        }
        this.validator = new SimpleReactValidator({
            autoForceUpdate: this,
            locale: "en",
            className: "text-danger",
            validators: {
                custom_phone_number: {
                    message: ':replaced',
                    rule: (val, params, validator) => {
                        return validateNumber(val).valid
                    },
                    messageReplace: (message, params) => {
                        return validateNumber(params[0]).message
                    },
                }
            }
        });
    }

    /* This lifecycle hook gets executed when the component mounts */
    componentDidMount() {
        this.fetchPractices();
    }

    handleDateChange = (selectedDate, modifiers, dayPickerInput) => {
        const {name} = dayPickerInput.props;
        this.setState(prevState => ({
            newUser: {
                ...prevState.newUser,
                [name]: selectedDate
            }
        }));
    };

    handleRadioChange = e => {
        let name = e.target.name;
        let value = e.target.value;
        this.setState(prevState => ({
            newUser: {
                ...prevState.newUser,
                [name]: value
            }
        }));
    };

    handleInputChange = e => {
        let value = e.target.value;
        let name = e.target.name;
        this.setState(prevState => ({
            newUser: {
                ...prevState.newUser,
                [name]: value
            }
        }));
    };

    handleCheckBox = e => {
        const {name, value} = e.target;
        const {newUser} = this.state;
        let values = newUser[name] || [];
        if (values.indexOf(value) > -1) {
            values = values.filter(s => s !== value);
        } else {
            values.push(value);
        }

        this.setState(prevState => ({
            newUser: {...prevState.newUser, [name]: values}
        }));
    };

    handleFormSubmit = e => {
        e.preventDefault();

        const {csrfToken, parentDomain} = this.props;
        let userData = this.state.newUser;

        this.setState({initialState: false});

        // check some methods here to ensure we never send 'other' data if the linked question doesn't show it
        if (!this.isOtherMeansOfContact()) {
            delete userData.otherMeansOfContact;
        }
        if (!this.isOtherNhs()) {
            delete userData.otherNhs;
        }
        if (!this.isOtherMedicationSelected()) {
            delete userData.otherPrescribedMedication;
        }
        if (!this.isReceivingPrescribedMedication()) {
            delete userData.prescribedMedications;
        }

        FetchHelper.fetchWithPost({
            context: this,
            csrfToken,
            parentDomain,
            type: "nhs",
            body: userData
        });
    };

    fetchPractices = () => {
        this.setState({practicesLoading: true});

        fetch("/v1/organisations/practices", {
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": this.props.csrfToken
            }
        })
            .then(response => {
                return response.json().then(data => {
                    return {
                        data: data,
                        status: response.status
                    };
                });
            })
            .then(response => {
                switch (response.status) {
                    case 200:
                        this.setState({
                            practiceOptions: response.data,
                            practicesLoading: false
                        });
                        break;
                    default:
                        console.log("Unexpected status: " + response.status);
                        window.location.assign("/500.html");
                }
            })
            .catch(exception => {
                this.setState({practicesLoading: false});
                console.log(exception);
                window.location.assign("/500.html");
            });
    };

    fetchPrescribedMedications = () => {
        this.setState({medicationsLoading: true});

        fetch("/v1/medications", {
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": this.props.csrfToken
            }
        })
            .then(response => {
                return response.json().then(data => {
                    return {
                        data: data,
                        status: response.status
                    };
                });
            })
            .then(response => {
                switch (response.status) {
                    case 200:
                        this.setState({
                            medicationOptions: response.data,
                            medicationsLoading: false
                        });
                        break;
                    default:
                        console.log("Unexpected status: " + response.status);
                        window.location.assign("/500.html");
                }
            })
            .catch(({exception}) => {
                this.setState({medicationsLoading: false});
                console.log(exception);
                window.location.assign("/500.html");
            });
    };

    renderSubmitButton = () => {
        const {initialState} = this.state;
        const valid = initialState || this.validator.allValid();
        return (
            <>
                {!valid && (
                    <div className="alert alert-danger">
                        There are unresolved form errors, please correct the form before
                        resubmitting.
                    </div>
                )}
                <Button type="submit" className="btn-primary " disabled={!valid}>
                    Submit
                </Button>
            </>
        );
    };

    renderPrescribedMedications = () => {
        const {
            prescribedMedications,
            otherPrescribedMedication
        } = this.state.newUser;

        const {medicationOptions, medicationsLoading} = this.state;

        if (medicationsLoading) {
            return <p>Loading...</p>;
        } else if (this.isReceivingPrescribedMedication() && medicationOptions) {
            return (
                <>
                    <div className="form-group">
                        <CheckBox
                            name="prescribedMedications"
                            label="Please select the prescribed psychotropic medication being received"
                            required
                            onChange={this.handleCheckBox}
                            options={medicationOptions}
                            selectedOptions={prescribedMedications}
                        />
                    </div>

                    {this.isOtherMedicationSelected() && (
                        <>
                            <div className="form-group">
                                <Input
                                    name="otherPrescribedMedication"
                                    label="Other Prescribed medication"
                                    value={otherPrescribedMedication}
                                    required
                                    onChange={this.handleInputChange}
                                />
                            </div>
                        </>
                    )}
                </>
            );
        }
    };

    isOtherMeansOfContact = () => {
        const {preferredMeansOfContact} = this.state.newUser;
        return preferredMeansOfContact === "Other";
    };

    isOtherNhs = () => {
        const {treatedByOtherNhs} = this.state.newUser;
        return treatedByOtherNhs === "Yes";
    };

    isReceivingPrescribedMedication = () => {
        const {receivingPrescribedMedication} = this.state.newUser;
        return receivingPrescribedMedication === "Yes";
    };

    isOtherMedicationSelected = () => {
        const {prescribedMedications} = this.state.newUser;
        const {medicationOptions} = this.state;

        if (medicationOptions) {
            let otherId = medicationOptions.filter(a => {
                return (a.description || "").toLowerCase() === "other";
            });
            // this should never be null, unless the data has changed and we've removed 'Other' from the table :/
            otherId = otherId.length > 0 ? otherId[0].id.toString() : null;
            return prescribedMedications && prescribedMedications.includes(otherId);
        }
        return false;
    };

    render() {
        const {
            practice,
            referredBy,
            referrerContactInformation,
            email,
            firstName,
            lastName,
            gender,
            nhsNumber,
            address1,
            address2,
            address3,
            address4,
            postcode,
            dateOfBirth,
            homeTelephone,
            mobileTelephone,
            workTelephone,
            referralReason,
            preferredMeansOfContact,
            otherMeansOfContact,
            receivingPrescribedMedication,
            treatedByOtherNhs,
            otherNhs
        } = this.state.newUser;

        const {
            practiceOptions,
            practicesLoading,
            medicationOptions,
            medicationsLoading,
            genderOptions,
            contactOptions,
            yesNoOptions,
            submitted
        } = this.state;

        return (
            <div className="container">
                <h1>
                    Therapeutic Counselling Services
                    <small>Referral Form for counselling via the NHS</small>
                </h1>
                <div className="row justify-content-start">
                    {submitted ? (
                        <Confirmation
                            referralType="nhs"
                            returnCallback={() => {
                                this.setState({
                                    submitted: false,
                                    newUser: {system: "NHS"},
                                    initialState: true
                                });
                            }}
                        />
                    ) : (
                        <form className="col-8" onSubmit={this.handleFormSubmit} noValidate>
                            <div className="alert alert-secondary">
                                <p>
                                    Fields marked with <span className="required-field">*</span>{" "}
                                    are mandatory.
                                </p>
                            </div>
                            <div className="form-group">
                                <Select
                                    label="Practice"
                                    name="practice"
                                    options={practiceOptions}
                                    value={practice}
                                    placeholder={"Select Practice"}
                                    handleChange={this.handleInputChange}
                                    loading={practicesLoading}
                                    required
                                />{" "}
                                {this.validator.message("practice", practice, "required")}
                            </div>
                            <div className="form-group">
                                <Input
                                    label="Referrer's Name"
                                    name="referredBy"
                                    value={referredBy}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "Referrer's name",
                                    referredBy,
                                    "required|alpha_space"
                                )}
                            </div>
                            <div className="form-group">
                                <TextArea
                                    label="Referrer's Contact Information"
                                    name="referrerContactInformation"
                                    value={referrerContactInformation}
                                    required={true}
                                    maxLength={2000}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "Referrer's Contact Information",
                                    referrerContactInformation,
                                    "required"
                                )}
                            </div>
                            <div className="form-group">
                                <EmailInput
                                    label={"Email address"}
                                    name="email"
                                    id="email"
                                    value={email}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message(
                                    "email address",
                                    email,
                                    "required|email"
                                )}
                            </div>
                            <div className="form-group">
                                <Input
                                    label="First Name"
                                    name="firstName"
                                    value={firstName}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "first name",
                                    firstName,
                                    "required|alpha_space"
                                )}
                            </div>
                            <div className="form-group">
                                <Input
                                    label="Last Name"
                                    name="lastName"
                                    value={lastName}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "last name",
                                    lastName,
                                    "required|alpha_space"
                                )}
                            </div>
                            <div className="form-group">
                                <Input
                                    name="dateOfBirth"
                                    label="Date of Birth (DD/MM/YYYY)"
                                    value={dateOfBirth}
                                    required={true}
                                    type="date"
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "date of birth",
                                    dateOfBirth,
                                    "required"
                                )}
                            </div>
                            <div className="form-group">
                                <Input
                                    type="text"
                                    label="NHS Number"
                                    name="nhsNumber"
                                    value={nhsNumber}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "nhs number",
                                    nhsNumber,
                                    "required|alpha_num_space"
                                )}
                            </div>
                            <h2>Address</h2>
                            <div className="form-group">
                                <Input
                                    name="address1"
                                    label="Line 1"
                                    value={address1}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message("address line 1", address1, "required")}
                            </div>
                            <div className="form-group">
                                <Input
                                    name="address2"
                                    label="Line 2"
                                    value={address2}
                                    onChange={this.handleInputChange}
                                />{" "}
                            </div>
                            <div className="form-group">
                                <Input
                                    name="address3"
                                    label="Line 3"
                                    value={address3}
                                    onChange={this.handleInputChange}
                                />{" "}
                            </div>
                            <div className="form-group">
                                <Input
                                    name="address4"
                                    label="Line 4"
                                    value={address4}
                                    onChange={this.handleInputChange}
                                />{" "}
                            </div>
                            <div className="form-group">
                                <Input
                                    name="postcode"
                                    label="Postcode"
                                    value={postcode}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "postcode",
                                    postcode,
                                    "required|alpha_num_space"
                                )}
                            </div>
                            <div className="form-group">
                                <TelephoneInput
                                    name="homeTelephone"
                                    label={"Home telephone"}
                                    value={homeTelephone}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "Home telephone",
                                    homeTelephone,
                                    `custom_phone_number:${homeTelephone}`,
                                )}
                            </div>
                            <div className="form-group">
                                <TelephoneInput
                                    name="workTelephone"
                                    label={"Work telephone"}
                                    value={workTelephone}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "Work telephone",
                                    workTelephone,
                                    `custom_phone_number:${workTelephone}`,
                                )}
                            </div>
                            <div className="form-group">
                                <TelephoneInput
                                    name="mobileTelephone"
                                    label={"Mobile telephone"}
                                    value={mobileTelephone}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "Mobile telephone",
                                    mobileTelephone,
                                    `custom_phone_number:${mobileTelephone}`,
                                )}
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="Gender"
                                    name="gender"
                                    options={genderOptions}
                                    value={gender}
                                    onChange={this.handleInputChange}
                                />{" "}
                            </div>
                            <div className="form-group">
                                <Input
                                    name="referralReason"
                                    label="Reason For Referral"
                                    value={referralReason}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message(
                                    "reason for referral",
                                    referralReason,
                                    "required"
                                )}
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="Patient’s preferred means of being contacted"
                                    name="preferredMeansOfContact"
                                    options={contactOptions}
                                    value={preferredMeansOfContact}
                                    onChange={this.handleInputChange}
                                />{" "}
                            </div>
                            {this.isOtherMeansOfContact() && (
                                <div className="form-group">
                                    <Input
                                        name="otherMeansOfContact"
                                        label="Other means of contact"
                                        value={otherMeansOfContact}
                                        required={true}
                                        onChange={this.handleInputChange}
                                    />
                                </div>
                            )}
                            <div className="form-group">
                                <RadioButton
                                    label="Is the Patient currently receiving prescribed psychotropic medication?"
                                    name="receivingPrescribedMedication"
                                    options={yesNoOptions}
                                    value={receivingPrescribedMedication}
                                    onChange={e => {
                                        this.handleInputChange(e);
                                        // if we haven't loaded the medications yet, do that here
                                        if (!medicationOptions && !medicationsLoading) {
                                            this.fetchPrescribedMedications();
                                        }
                                    }}
                                    required
                                />{" "}
                            </div>
                            {this.renderPrescribedMedications()}
                            <div className="form-group ">
                                <RadioButton
                                    label="Has the patient been treated for this condition by any other NHS Mental Health Service?"
                                    name="treatedByOtherNhs"
                                    options={yesNoOptions}
                                    value={treatedByOtherNhs}
                                    onChange={this.handleInputChange}
                                    required
                                />{" "}
                            </div>
                            {this.isOtherNhs() && (
                                <>
                                    <div className="form-group">
                                        <Input
                                            name="otherNhs"
                                            label="Other NHS Mental Health Service"
                                            value={otherNhs}
                                            required
                                            onChange={this.handleInputChange}
                                        />
                                    </div>
                                </>
                            )}
                            {this.renderSubmitButton()}
                        </form>
                    )}
                            <ResearchDisclaimer />
                </div>
            </div>
        );
    }
}

export default NhsReferral;
